/**
 * CreateApplicationModal
 *
 * Modal for creating/editing credit applications.
 * - Lender dropdown from GET /api/crm/lenders
 * - Lender Product dropdown filtered by selected lender
 * - Application type select
 * - Requested amount (currency input)
 * - Notes textarea
 *
 * AC003: Create application modal
 * AC004: Edit application modal (pre-populated)
 */

import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const APPLICATION_TYPE_OPTIONS = [
  { value: "new", label: "New" },
  { value: "variation", label: "Variation" },
  { value: "refinance", label: "Refinance" },
  { value: "top_up", label: "Top Up" },
  { value: "restructure", label: "Restructure" },
];

interface Lender {
  id: string;
  lenderName: string;
}

interface LenderProduct {
  id: string;
  productName: string;
}

interface EditingApplication {
  id: string;
  lenderId: string | null;
  lenderProductId: string | null;
  applicationType: string;
  requestedAmount: string | null;
  notes: string | null;
}

interface CreateApplicationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  editingApplication?: EditingApplication | null;
}

export function CreateApplicationModal({
  open,
  onOpenChange,
  dealId,
  editingApplication,
}: CreateApplicationModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [lenderId, setLenderId] = useState<string>("none");
  const [lenderProductId, setLenderProductId] = useState<string>("none");
  const [applicationType, setApplicationType] = useState<string>("new");
  const [requestedAmount, setRequestedAmount] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const isEditing = !!editingApplication;

  // Reset form when modal opens / editing changes
  useEffect(() => {
    if (open) {
      if (editingApplication) {
        setLenderId(editingApplication.lenderId || "none");
        setLenderProductId(editingApplication.lenderProductId || "none");
        setApplicationType(editingApplication.applicationType || "new");
        setRequestedAmount(editingApplication.requestedAmount || "");
        setNotes(editingApplication.notes || "");
      } else {
        setLenderId("none");
        setLenderProductId("none");
        setApplicationType("new");
        setRequestedAmount("");
        setNotes("");
      }
    }
  }, [open, editingApplication]);

  // Fetch lenders — uses default getQueryFn (no custom queryFn)
  const { data: lenders = [] } = useQuery<Lender[]>({
    queryKey: ["/api/crm/lenders"],
    enabled: open,
  });

  // Fetch lender products filtered by selected lender
  const selectedLenderId = lenderId !== "none" ? lenderId : null;
  const { data: lenderProducts = [] } = useQuery<LenderProduct[]>({
    queryKey: [`/api/crm/lenders/${selectedLenderId}/products`],
    enabled: open && !!selectedLenderId,
  });

  // Reset product when lender changes
  useEffect(() => {
    if (!editingApplication) {
      setLenderProductId("none");
    }
  }, [lenderId, editingApplication]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/applications", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/applications"] });
      toast({ title: "Application created" });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create application",
        variant: "destructive",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/applications/${editingApplication?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/applications"] });
      toast({ title: "Application updated" });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update application",
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    const payload: Record<string, unknown> = {
      dealId,
      applicationType,
      lenderId: lenderId !== "none" ? lenderId : null,
      lenderProductId: lenderProductId !== "none" ? lenderProductId : null,
      requestedAmount: requestedAmount || null,
      notes: notes || null,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="dialog-create-application">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Application" : "Create Application"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Lender Select */}
          <div className="space-y-1.5">
            <Label htmlFor="lender-select">Lender</Label>
            <Select value={lenderId} onValueChange={setLenderId}>
              <SelectTrigger data-testid="select-lender">
                <SelectValue placeholder="Select a lender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No lender</SelectItem>
                {lenders.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.lenderName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lender Product Select (filtered by lender) */}
          {selectedLenderId && (
            <div className="space-y-1.5">
              <Label htmlFor="product-select">Lender Product</Label>
              <Select value={lenderProductId} onValueChange={setLenderProductId}>
                <SelectTrigger data-testid="select-lender-product">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No product</SelectItem>
                  {lenderProducts.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.productName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Application Type */}
          <div className="space-y-1.5">
            <Label htmlFor="type-select">Application Type</Label>
            <Select value={applicationType} onValueChange={setApplicationType}>
              <SelectTrigger data-testid="select-application-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {APPLICATION_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Requested Amount */}
          <div className="space-y-1.5">
            <Label htmlFor="amount-input">Requested Amount</Label>
            <Input
              id="amount-input"
              type="number"
              placeholder="e.g. 500000"
              value={requestedAmount}
              onChange={(e) => setRequestedAmount(e.target.value)}
              data-testid="input-requested-amount"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="notes-input">Notes</Label>
            <Textarea
              id="notes-input"
              placeholder="Add any notes about this application..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="input-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            data-testid="button-cancel-application"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending}
            data-testid="button-submit-application"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Save Changes" : "Create Application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
